/**
 * Widget Embed Page
 *
 * This page is loaded inside the iframe when the widget is opened.
 * It parses URL parameters and renders the EmbeddedChat component.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import EmbeddedChat from '../components/EmbeddedChat';

const WidgetEmbedPage = () => {
  const [searchParams] = useSearchParams();

  // Apply widget-specific styles for iframe layout
  useEffect(() => {
    const htmlElement = document.documentElement;
    const bodyElement = document.body;
    const rootElement = document.getElementById('root');

    // Store original values
    const originalScrollbarGutter = htmlElement.style.scrollbarGutter;
    const originalHtmlOverflow = htmlElement.style.overflowY;

    // Fix: Remove scrollbar-gutter that causes right padding
    htmlElement.style.setProperty('scrollbar-gutter', 'auto', 'important');
    htmlElement.style.setProperty('overflow-y', 'hidden', 'important');

    // Set full height
    htmlElement.style.height = '100%';
    bodyElement.style.height = '100%';
    bodyElement.style.margin = '0';
    bodyElement.style.overflow = 'hidden';
    if (rootElement) {
      rootElement.style.height = '100%';
    }

    // Cleanup on unmount
    return () => {
      htmlElement.style.scrollbarGutter = originalScrollbarGutter;
      htmlElement.style.overflowY = originalHtmlOverflow;
    };
  }, []);
  const [config, setConfig] = useState({
    botId: '',
    apiOrigin: '',
    welcomeMessage: 'How can I help you?',
    themeColor: '#3B82F6',
    maxMessageLength: 2000,
  });

  useEffect(() => {
    // Parse URL parameters
    const botId = searchParams.get('botId') || '';
    const apiOrigin = searchParams.get('apiOrigin') || window.location.origin;
    const welcomeMessage = searchParams.get('welcomeMessage') || 'How can I help you?';
    const themeColor = searchParams.get('themeColor') || '#3B82F6';
    const maxMessageLength = parseInt(searchParams.get('maxMessageLength') || '2000', 10);

    setConfig({
      botId,
      apiOrigin,
      welcomeMessage: decodeURIComponent(welcomeMessage),
      themeColor,
      maxMessageLength,
    });

    // Send ready message to parent window
    window.parent.postMessage({ type: 'KMS_WIDGET_READY' }, '*');

    // Listen for messages from parent
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'KMS_WIDGET_CLOSE') {
        // Handle close request from parent
        window.parent.postMessage({ type: 'KMS_WIDGET_CLOSED' }, '*');
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [searchParams]);

  const handleClose = () => {
    // Send close message to parent window
    window.parent.postMessage({ type: 'KMS_WIDGET_CLOSE_REQUESTED' }, '*');
  };

  if (!config.botId) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600">Invalid widget configuration</p>
          <p className="text-sm text-gray-500 mt-2">Missing botId parameter</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-hidden m-0 p-0">
      <EmbeddedChat
        botId={config.botId}
        apiOrigin={config.apiOrigin}
        welcomeMessage={config.welcomeMessage}
        themeColor={config.themeColor}
        maxMessageLength={config.maxMessageLength}
        onClose={handleClose}
      />
    </div>
  );
};

export default WidgetEmbedPage;
